import { useMemo } from 'react'
import { Alert, Space, Table, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { WarningOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError, type Translate } from '@/shared/api/errors'
import { formatDateTime, formatDuration, formatNumber, utcTooltip } from '@/shared/lib/format'
import {
  HEARTBEAT_STALE_SECONDS,
  SCAN_STALE_SECONDS,
  fetchJobAlerts,
  groupAlerts,
  watchFreshness,
  type JobAlert,
  type JobAlertKind,
} from './jobsApi'

/** Reestr bilan bir xil davr — ikkalasi bitta sahifada, ajralib turmasin */

/**
 * Rang — turning DARAJASI bo'yicha (`JOB_FAULT_ORDER`).
 *
 * ⚠️ `OVERDUE` `FAILED` dan qattiqroq ohangda emas, ikkalasi ham qizil:
 * darajani rang bilan chizishga urinish ikkinchi qizil ohang talab
 * qilardi va u ekranda farq bermasdi. Daraja TARTIBDA ko'rinadi.
 */
const KIND_COLOR: Record<JobAlertKind, string> = {
  NEVER_RAN: 'red',
  OVERDUE: 'red',
  FAILED: 'volcano',
  NO_SUCCESS: 'orange',
  RECOVERED: 'green',
  HEARTBEAT: 'blue',
}

interface GroupedRow extends JobAlert {
  rowKey: string
}

/**
 * Sana ko'rinadigan parametrlar — ISO qiymat TENANT zonasida chiziladi.
 *
 * ⚠️ Ro'yxat ANIQ, «har qanday ISO ga o'xshash qiymatni formatla»
 * degan taxmin EMAS. Taxminiy aniqlash bir kun kelib xato matnning
 * ichidagi sanaga ham tegib ketardi (`error` parametrida server
 * xabari bo'lishi mumkin), va o'sha lahzada operator xatoning YARIM
 * o'zgartirilgan matnini ko'rardi.
 */
const DATE_PARAMS = ['startedAt', 'lastStartedAt', 'seenAt', 'due', 'since'] as const

/** Server reason text is Uzbek only, so other languages drop the free-text variants and parameters. */
const FREE_TEXT_REASON: Readonly<Record<string, string>> = {
  FAILED_ERROR: 'FAILED',
  NO_SUCCESS_MESSAGE: 'NO_SUCCESS',
}

function reasonText(row: JobAlert, t: Translate): string {
  const uzbek = t('common.langCode', { defaultValue: '' }) === 'uz'
  const fallback = uzbek && row.reason ? row.reason : t(`jobAlerts.kindHint.${row.kind}`, { defaultValue: '' })
  if (row.reasonCode === null) return fallback

  const params: Record<string, string> = { ...row.reasonParams }
  for (const key of DATE_PARAMS) {
    if (params[key]) params[key] = formatDateTime(params[key])
  }
  if (params['outcome']) params['outcome'] = t(`jobs.outcome.${params['outcome']}`, { defaultValue: params['outcome'] })

  const code = uzbek ? row.reasonCode : (FREE_TEXT_REASON[row.reasonCode] ?? row.reasonCode)
  const translated = t(`jobAlerts.reason.${code}`, { ...params, defaultValue: '' })
  return translated === '' ? fallback : translated
}

/**
 * Ogohlantirish qorovuli — «buni kimdir KO'RDIMI».
 *
 * <h2>⚠️ NEGA BU REESTR JADVALIDAN AJRATILGAN</h2>
 *
 * Reestr «job qanday» degan savolga javob beradi. Bu blok bir daraja
 * yuqorida turadi: nosozlik reestrda KO'RINIB TURGANI bilan hech kimga
 * xabar berilmagan bo'lishi mumkin — va aynan shu farq `BillingJob` ni
 * 22 daqiqa jim tutgan edi.
 *
 * <h2>⚠️ `lastScanAt` — BLOKNING BOSH MAYDONI</h2>
 *
 * U har qanday holatda chiziladi: ochiq ogohlantirish bo'lsa ham,
 * bo'lmasa ham, so'rov yiqilsa ham. Sabab — qorovul o'z o'limi haqida
 * xabar bera olmaydi, ya'ni jimlikning ikki ma'nosi bor: «hammasi
 * joyida» va «qorovul o'lgan». Ularni faqat shu sana ajratadi. Uni
 * «ogohlantirish yo'q» holatida yashirish — sahifaning butun maqsadini
 * yo'q qilish bilan teng.
 *
 * <h2>⚠️ Jimlikning ikkinchi, MUSTAQIL o'lchovi</h2>
 *
 * `lastHeartbeatAt` — sutkalik «tirikman» xabari. U Telegram guruhiga
 * boradi, ya'ni bu panelga umuman kirmasdan ham ko'rinadi. Shuning
 * uchun ikkalasi yonma-yon turadi: biri panelning, ikkinchisi
 * guruhning ko'zi.
 */
export function JobAlertsPanel() {
  const { t } = useTranslation()

  const alerts = useQuery({
    queryKey: queryKeys.jobs.alerts,
    queryFn: ({ signal }) => fetchJobAlerts(signal),
  // ⚠️ Avtoyangilanish OLIB TASHLANDI (egasi qarori 2026-08-20):
  //    u faqat bosh sahifada qoladi. Qolgan ekranlarda u backendga
  //    doimiy yuk berardi, holbuki operator sahifani ochiq qoldirib
  //    boshqa ish bilan band bo'ladi. Yangilash QO'LDA — tugma bor.
  })

  const data = alerts.data ?? null
  const grouped = useMemo(() => groupAlerts(data?.open ?? []), [data])

  /**
   * ⚠️ Soat — JAVOB KELGAN lahza (`dataUpdatedAt`), chizish lahzasi
   * emas. `Date.now()` ni chizish ichida chaqirish har qayta chizishda
   * boshqa natija berardi: bitta ma'lumot goh yashil, goh qizil
   * ko'rinardi va operator o'zi bosgan tugmani aybdor deb o'ylardi.
   * Ro'yxat har daqiqada yangilanadi, ya'ni bu soat ham yuradi.
   */
  const nowMs = alerts.dataUpdatedAt || Date.now()

  const scan = watchFreshness(data?.lastScanAt ?? null, nowMs, SCAN_STALE_SECONDS)
  const heartbeat = watchFreshness(
    data?.lastHeartbeatAt ?? null,
    nowMs,
    HEARTBEAT_STALE_SECONDS,
  )

  const rows = useMemo<GroupedRow[]>(() => {
    const out: GroupedRow[] = []
    for (const group of grouped.faults) {
      for (const row of group.rows) out.push({ ...row, rowKey: `${row.service}/${row.job}` })
    }
    for (const row of grouped.recovered) {
      out.push({ ...row, rowKey: `${row.service}/${row.job}` })
    }
    return out
  }, [grouped])

  const columns = useMemo<ColumnsType<GroupedRow>>(
    () => [
      {
        key: 'kind',
        title: t('jobAlerts.columns.kind'),
        width: 180,
        render: (_, row) =>
          row.recovered ? (
            // ⚠️ Sog'aygan qator ochiq ro'yxatda QOLADI — u faqat
            // «tiklandi» xabari yuborilgandan keyin o'chadi. Uni
            // nosozlik deb chizish ekranda yo'q muammoni ko'rsatardi.
            <Tooltip title={t('jobAlerts.recoveredHint')}>
              <Tag color={KIND_COLOR.RECOVERED}>{t('jobAlerts.recovered')}</Tag>
            </Tooltip>
          ) : (
            <Tooltip title={t(`jobAlerts.kindHint.${row.kind}`)}>
              <Tag color={KIND_COLOR[row.kind]} icon={<WarningOutlined />}>
                {t(`jobAlerts.kind.${row.kind}`)}
              </Tag>
            </Tooltip>
          ),
      },
      {
        key: 'job',
        title: t('jobs.columns.job'),
        width: 240,
        render: (_, row) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{row.job}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {row.service}
            </Typography.Text>
          </Space>
        ),
      },
      {
        key: 'notified',
        title: t('jobAlerts.columns.notified'),
        width: 220,
        render: (_, row) =>
          row.notifiedAt === null ? (
            /*
              ⚠️ SAHIFADAGI IKKINCHI ENG JIM HOLAT: nosozlik topilgan,
              qator bor, lekin guruhga xabar KETMAGAN. Bo'sh katak
              qoldirilsa bu «hali navbatda» bilan «yuborilmadi va
              hech kim bilmaydi» ni bir xil ko'rsatardi.
            */
            <Typography.Text type="danger">{t('jobAlerts.notSent')}</Typography.Text>
          ) : (
            <Space direction="vertical" size={0}>
              <Typography.Text title={utcTooltip(row.notifiedAt)}>
                {formatDateTime(row.notifiedAt)}
              </Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('jobAlerts.notifyCount', { count: row.notifyCount })}
              </Typography.Text>
            </Space>
          ),
      },
      {
        key: 'firstSeenAt',
        title: t('jobAlerts.columns.firstSeen'),
        width: 200,
        render: (_, row) => (
          <Typography.Text title={utcTooltip(row.firstSeenAt)}>
            {formatDateTime(row.firstSeenAt)}
          </Typography.Text>
        ),
      },
      {
        key: 'reason',
        title: t('jobAlerts.columns.reason'),
        render: (_, row) => {
          const text = reasonText(row, t)
          return text === '' ? (
            t('common.dash')
          ) : (
            <Typography.Text ellipsis={{ tooltip: text }}>{text}</Typography.Text>
          )
        },
      },
    ],
    [t],
  )

  return (
    <div style={{ marginBottom: 24 }}>
      <Typography.Title level={5} style={{ marginTop: 0 }}>
        {t('jobAlerts.title')}
      </Typography.Title>
      <Typography.Paragraph type="secondary">{t('jobAlerts.intro')}</Typography.Paragraph>

      {alerts.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 8 }}
          message={translateError(alerts.error, t)}
        />
      ) : null}

      {/*
        ⚠️ QOROVULNING O'Z HOLATI — ro'yxatdan YUQORIDA va DOIM
        ko'rinadi. `JobController.alerts` javadoc'i shuni talab qiladi
        va sabab oddiy: eskirgan skanerda pastdagi bo'sh ro'yxat
        «hammasi joyida» degan YOLG'ON javob bo'lardi.
      */}
      {scan === 'FRESH' ? (
        <Space size={8} wrap style={{ marginBottom: 12 }}>
          <Tag color="green">{t('jobAlerts.watchAlive')}</Tag>
          <Typography.Text type="secondary" title={utcTooltip(data?.lastScanAt)}>
            {t('jobAlerts.lastScan', { at: formatDateTime(data?.lastScanAt) })}
          </Typography.Text>
          <Typography.Text type="secondary">
            {t('jobAlerts.counters', {
              scans: formatNumber(data?.scans ?? 0),
              sent: formatNumber(data?.alertsSent ?? 0),
            })}
          </Typography.Text>
        </Space>
      ) : (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            scan === 'NEVER'
              ? t('jobAlerts.scanNeverTitle')
              : t('jobAlerts.scanStaleTitle', {
                  duration: formatDuration(
                    Math.round((nowMs - Date.parse(data?.lastScanAt ?? '')) / 1000),
                  ),
                })
          }
          description={
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Typography.Text>{t('jobAlerts.scanStaleHint')}</Typography.Text>
              {data?.lastScanAt ? (
                <Typography.Text type="secondary" title={utcTooltip(data.lastScanAt)}>
                  {t('jobAlerts.lastScan', { at: formatDateTime(data.lastScanAt) })}
                </Typography.Text>
              ) : null}
            </Space>
          }
        />
      )}

      {/*
        ⚠️ IKKINCHI, MUSTAQIL SIGNAL. «Tirikman» xabari Telegram
        guruhiga boradi — ya'ni u bu panelga umuman kirmasdan ham
        ko'rinadi. Skaner yurib turgan, lekin xabar chiqmayotgan holat
        BOR: yuborish zanjiri uzilgan bo'ladi va uni faqat shu qator
        ochib beradi.
      */}
      {heartbeat === 'FRESH' ? (
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          <span title={utcTooltip(data?.lastHeartbeatAt)}>
            {t('jobAlerts.lastHeartbeat', { at: formatDateTime(data?.lastHeartbeatAt) })}
          </span>
        </Typography.Paragraph>
      ) : (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={
            heartbeat === 'NEVER'
              ? t('jobAlerts.heartbeatNeverTitle')
              : t('jobAlerts.heartbeatStaleTitle')
          }
          description={t('jobAlerts.heartbeatHint')}
        />
      )}

      <Table<GroupedRow>
        dataSource={rows}
        columns={columns}
        rowKey={(row) => row.rowKey}
        loading={alerts.isPending}
        size="small"
        pagination={false}
        scroll={{ x: 1100 }}
        locale={{
          emptyText: (
            /*
              ⚠️ «Ochiq ogohlantirish yo'q» YOLG'IZ ma'no bermaydi va
              matn buni ochiq aytadi: xulosa faqat yuqoridagi skaner
              sanasi bilan BIRGA o'qiladi. Shuning uchun bu yerda
              «hammasi joyida» degan so'z YO'Q.
            */
            <Typography.Text type="secondary">
              {alerts.isError ? t('jobAlerts.loadFailed') : t('jobAlerts.empty')}
            </Typography.Text>
          ),
        }}
      />
    </div>
  )
}
